import { ExternalLink } from 'lucide-react';
import type { Confidence, SourceRef, WellStatus } from '../types';

// Shared WWD atoms. ConfidenceValue is THE load-bearing component: every fact in WWD
// renders through it so the four confidence tiers (data-quality core in types.ts) are
// visually unmistakable on every tab.

/**
 * Renders a fact according to its confidence tier:
 *  - confirmed      → normal text
 *  - estimated      → orange + " (est.)" suffix, estBasis as tooltip
 *  - reported       → normal text + small "reported" hint
 *  - not_disclosed  → grey italic "Not publicly disclosed" (also when value is null)
 */
export function ConfidenceValue({
  value,
  confidence,
  estBasis,
}: {
  value: string | null;
  confidence: Confidence;
  estBasis?: string | null;
}) {
  if (confidence === 'not_disclosed' || value === null) {
    return <span className="text-muted italic opacity-70">Not publicly disclosed</span>;
  }
  if (confidence === 'estimated') {
    return (
      <span className="text-amber-500" title={estBasis ?? undefined}>
        {value}
        <span className="text-xs"> (est.)</span>
      </span>
    );
  }
  if (confidence === 'reported') {
    return (
      <span>
        {value}
        <span className="ml-1 text-[0.65rem] uppercase tracking-wider text-muted font-(family-name:--font-ui)">
          reported
        </span>
      </span>
    );
  }
  return <span>{value}</span>;
}

/** Small external-link anchor pointing at the mandated deep link of a SourceRef. */
export function SourceLink({ source }: { source: SourceRef }) {
  const title = source.documentTitle + (source.pageRef ? ' — ' + source.pageRef : '');
  return (
    <a
      href={source.url}
      target="_blank"
      rel="noreferrer"
      title={title}
      aria-label={'Source: ' + title}
      className="inline-flex items-center align-middle text-accent hover:text-accent-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
    >
      <ExternalLink className="h-3.5 w-3.5" aria-hidden />
    </a>
  );
}

/** The 5 map colors (+ other) — exact semantic colors from the product spec. */
export const WELL_STATUS_COLORS: Record<WellStatus, string> = {
  drilling: '#22c55e',
  discovery: '#06b6d4',
  dry: '#ef4444',
  permitted: '#eab308',
  planned: '#14b8a6',
  other: '#64748b',
};

export const WELL_STATUS_LABELS: Record<WellStatus, string> = {
  drilling: 'Drilling',
  discovery: 'Discovery',
  dry: 'Dry',
  permitted: 'Permitted',
  planned: 'Planned',
  other: 'Other',
};

export function StatusDot({ status }: { status: WellStatus }) {
  return (
    <span
      aria-hidden
      className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full"
      style={{ backgroundColor: WELL_STATUS_COLORS[status] }}
    />
  );
}

/** ISO-3166 alpha-2 → regional-indicator emoji. Pure function, no dependency. */
export function flagEmoji(countryCode: string): string {
  const cc = countryCode.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return '';
  return String.fromCodePoint(
    0x1f1e6 + (cc.charCodeAt(0) - 65),
    0x1f1e6 + (cc.charCodeAt(1) - 65)
  );
}

export function Flag({ countryCode }: { countryCode: string | null }) {
  if (!countryCode) return null;
  const emoji = flagEmoji(countryCode);
  if (!emoji) return null;
  return (
    <span role="img" aria-label={countryCode} title={countryCode}>
      {emoji}
    </span>
  );
}
